import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface TicketData {
  id: string;
  ticketCode?: string;
  offenceType: string;
  violatorName: string;
  fineAmount: number;
  createdAt: any;
  status: string;
}

const ViewerTicketAuth: React.FC = () => {
  const [ticketCode, setTicketCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [violatorName, setViolatorName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [verifiedTicket, setVerifiedTicket] = useState<TicketData | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  // Verify ticket code
  const verifyTicketCode = async () => {
    if (!ticketCode.trim()) return;

    console.log('🔍 Verifying ticket code:', ticketCode);
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      // Try with the code as-is first (matching iOS logic)
      console.log('📋 Trying code as-is:', ticketCode.toUpperCase());
      let ticketsRef = collection(db, 'trafficTickets');
      let q = query(ticketsRef, where('ticketCode', '==', ticketCode.toUpperCase()));
      let snapshot = await getDocs(q);
      
      // If not found and code doesn't start with "TKT-", try with "TKT-" prefix
      if (snapshot.empty && !ticketCode.toUpperCase().startsWith('TKT-')) {
        const prefixedCode = `TKT-${ticketCode.toUpperCase()}`;
        console.log('📋 Trying with TKT- prefix:', prefixedCode);
        q = query(ticketsRef, where('ticketCode', '==', prefixedCode));
        snapshot = await getDocs(q);
      }
      
      console.log('📊 Query results:', snapshot.docs.length, 'documents found');
      
      if (snapshot.empty) {
        console.log('❌ No ticket found for code:', ticketCode);
        throw new Error('Invalid ticket code. Please check and try again.');
      }
      
      const ticketDoc = snapshot.docs[0];
      const ticketData = { id: ticketDoc.id, ...ticketDoc.data() } as TicketData;
      
      console.log('✅ Ticket found:', ticketData.id, ticketData.violatorName, ticketData.offenceType);
      
      setVerifiedTicket(ticketData);
      setViolatorName(ticketData.violatorName);
      
      // Create viewerAccounts document (following iOS app pattern)
      await createViewerAccount(ticketCode);
      
    } catch (error: any) {
      console.log('❌ Error verifying ticket code:', error.message);
      setErrorMessage(error.message);
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Create viewerAccounts document (following iOS app pattern)
  const createViewerAccount = async (ticketCode: string) => {
    console.log('📝 Creating viewerAccounts document for ticket code:', ticketCode);
    
    try {
      // Use the actual ticket code from the verified ticket
      const actualTicketCode = verifiedTicket?.ticketCode || ticketCode;
      console.log('🎫 Using actual ticket code:', actualTicketCode);
      
      // Create viewerAccounts document
      const viewerAccountRef = doc(db, 'viewerAccounts', email);
      const viewerAccountData = {
        email: email,
        linkedTicketCodes: [actualTicketCode], // Add the verified ticket code
        lastUpdated: new Date()
      };
      
      console.log('📄 Viewer account data:', viewerAccountData);
      await setDoc(viewerAccountRef, viewerAccountData, { merge: true });
      console.log('✅ ViewerAccounts document created/updated');
      
    } catch (error: any) {
      console.log('❌ Error creating viewer account:', error.message);
      // Don't throw error - continue with ticket verification
    }
  };

  // Create account and link ticket
  const handleAuthentication = async () => {
    if (!email.trim() || !password.trim()) return;

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      setShowError(true);
      return;
    }

    console.log('🔐 Starting authentication for email:', email);
    console.log('👤 Violator name from ticket:', violatorName);

    setIsLoading(true);
    setErrorMessage('');

    try {
      // Create user account
      console.log('📝 Creating Firebase Auth account...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('✅ Firebase Auth account created:', user.uid);

      // Create user document in Firestore
      console.log('💾 Creating Firestore user document...');
      const userDocData = {
        uid: user.uid,
        id: user.uid, // Required by Firestore rules
        email: email,
        fullName: violatorName,
        role: 'viewer',
        createdAt: new Date(),
        isActive: true
      };
      
      console.log('📄 User document data:', userDocData);
      await setDoc(doc(db, 'users', user.uid), userDocData);
      console.log('✅ Firestore user document created');

      // Link ticket to user account
      if (verifiedTicket) {
        console.log('🔗 Linking ticket to user account...');
        console.log('🎫 Ticket ID:', verifiedTicket.id);
        console.log('👤 User ID:', user.uid);
        
        const ticketUpdateData = {
          viewerEmail: email,
          viewerId: user.uid,
          violatorEmail: user.uid, // Add this field for iOS app compatibility
          linkedAt: new Date()
        };
        
        console.log('📝 Ticket update data:', ticketUpdateData);
        await updateDoc(doc(db, 'trafficTickets', verifiedTicket.id), ticketUpdateData);
        console.log('✅ Ticket linked to user account');
      }

      console.log('🚀 Navigating to ticket details...');
      // Navigate to ticket details
      navigate(`/tickets/${verifiedTicket?.id}`);
      
    } catch (error: any) {
      console.log('❌ Authentication error:', error.code, error.message);
      
      // If user already exists, try to sign in and link ticket
      if (error.code === 'auth/email-already-in-use') {
        console.log('🔄 User already exists, trying to sign in...');
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;
          
          console.log('✅ User signed in successfully:', user.uid);

          // Check if user is a viewer
          console.log('🔍 Checking user role...');
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (!userDoc.exists()) {
            console.log('📝 User document doesn\'t exist, creating one...');
            // Create the user document since it doesn't exist
            const userDocData = {
              uid: user.uid,
              id: user.uid, // Required by Firestore rules
              email: email,
              fullName: violatorName,
              role: 'viewer',
              createdAt: new Date(),
              isActive: true
            };
            
            console.log('📄 Creating user document data:', userDocData);
            await setDoc(doc(db, 'users', user.uid), userDocData);
            console.log('✅ User document created for existing user');
          } else if (userDoc.data()?.role !== 'viewer') {
            console.log('❌ User exists but is not a viewer, role:', userDoc.data()?.role);
            throw new Error('This email is already registered with a different account type.');
          }
          
          console.log('✅ User is confirmed as viewer');

          // Link ticket to existing account
          if (verifiedTicket) {
            console.log('🔗 Linking ticket to existing account...');
            const ticketUpdateData = {
              viewerEmail: email,
              viewerId: user.uid,
              violatorEmail: user.uid, // Add this field for iOS app compatibility
              linkedAt: new Date()
            };
            
            console.log('📝 Ticket update data:', ticketUpdateData);
            await updateDoc(doc(db, 'trafficTickets', verifiedTicket.id), ticketUpdateData);
            console.log('✅ Ticket linked to existing account');
          }

          console.log('🚀 Navigating to ticket details...');
          navigate(`/tickets/${verifiedTicket?.id}`);
        } catch (signInError: any) {
          console.log('❌ Sign in error:', signInError.code, signInError.message);
          setErrorMessage(signInError.message || 'Failed to sign in. Please check your credentials.');
          setShowError(true);
        }
      } else {
        setErrorMessage(error.message);
        setShowError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-24 w-24 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="h-12 w-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Access Your Ticket
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your ticket code to view and pay your traffic ticket
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white shadow-xl rounded-lg p-8">
          {!verifiedTicket ? (
            // Ticket Code Verification
            <div className="space-y-6">
              <div>
                <label htmlFor="ticket-code" className="block text-sm font-medium text-gray-700 mb-2">
                  Ticket Code
                </label>
                <input
                  id="ticket-code"
                  type="text"
                  value={ticketCode}
                  onChange={(e) => setTicketCode(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter code (e.g., 2026-BE3A9Y)"
                  disabled={isLoading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the code from your traffic ticket
                </p>
              </div>

              <button
                onClick={verifyTicketCode}
                disabled={ticketCode.trim() === '' || isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verifying...
                  </div>
                ) : (
                  'Verify Ticket'
                )}
              </button>
            </div>
          ) : (
            // Account Creation & Ticket Details
            <div className="space-y-6">
              {/* Success Message */}
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">Ticket Verified!</h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>Ticket found for: <span className="font-semibold">{violatorName}</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticket Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-gray-900">Ticket Details</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Violation:</span>
                    <span className="text-sm font-medium">{verifiedTicket.offenceType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Fine Amount:</span>
                    <span className="text-sm font-medium">EC$ {verifiedTicket.fineAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className="text-sm font-medium capitalize">{verifiedTicket.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Access Code:</span>
                    <span className="text-sm font-medium">
                      {verifiedTicket.ticketCode}
                    </span>
                  </div>
                </div>
              </div>

              {/* Account Creation Form */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Create Account</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Create an account to access and pay your ticket online.
                  </p>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                      placeholder="Create a password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {showPassword ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        )}
                      </svg>
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Password must be at least 6 characters
                  </p>
                </div>

                <button
                  onClick={handleAuthentication}
                  disabled={email.trim() === '' || password.trim() === '' || isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Account...
                    </div>
                  ) : (
                    'Create Account & Access Ticket'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {showError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{errorMessage}</p>
                </div>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={() => setShowError(false)}
                    className="inline-flex bg-red-50 rounded-md p-1.5 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                  >
                    <svg className="h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewerTicketAuth;
